// SPDX-License-Identifier: AGPL-3.0-or-later

import {isChannelIconKey} from '@fluxer/constants/src/ChannelIcons';
import {z} from 'zod';

export const INSTANCE_ANNOUNCEMENT_TEXT_MAX = 200;
export const INSTANCE_ANNOUNCEMENT_BUTTON_TEXT_MAX = 40;
export const INSTANCE_ANNOUNCEMENT_URL_MAX = 512;
export const INSTANCE_ANNOUNCEMENT_TARGETS = ['none', 'shop', 'premium', 'url'] as const;
export const INSTANCE_ANNOUNCEMENT_TONES = ['purple', 'blue', 'green', 'orange', 'red', 'dark'] as const;

export type InstanceAnnouncementTone = (typeof INSTANCE_ANNOUNCEMENT_TONES)[number];

// Only a plain https link with a dotted host and no credentials; javascript:, data:, http: and the rest never pass.
export function isInstanceAnnouncementUrl(value: string): boolean {
	if (value.length > INSTANCE_ANNOUNCEMENT_URL_MAX || !value.startsWith('https://')) return false;
	try {
		const url = new URL(value);
		return url.protocol === 'https:' && url.hostname.includes('.') && url.username === '' && url.password === '';
	} catch {
		return false;
	}
}

const AnnouncementText = (max: number) => z.string().trim().min(1).max(max).nullable();

// Micup duyuru-bant: the one instance-wide announcement shown in the app's top bar; set in the admin.
const InstanceAnnouncementFields = z.object({
	enabled: z.boolean().describe('Whether the bar is shown at all'),
	icon: z
		.string()
		.refine(isChannelIconKey, 'Unknown icon')
		.nullable()
		.describe('A channel icon key (s: Solar or e: Fluent emoji) drawn before the text; null: no icon'),
	text_tr: AnnouncementText(INSTANCE_ANNOUNCEMENT_TEXT_MAX).describe('Turkish text, plain text'),
	text_en: AnnouncementText(INSTANCE_ANNOUNCEMENT_TEXT_MAX).describe(
		'English text, plain text; null: Turkish is shown to everyone',
	),
	button_text_tr: AnnouncementText(INSTANCE_ANNOUNCEMENT_BUTTON_TEXT_MAX).describe('Turkish button label'),
	button_text_en: AnnouncementText(INSTANCE_ANNOUNCEMENT_BUTTON_TEXT_MAX).describe(
		'English button label; null: the Turkish label',
	),
	target: z
		.enum(INSTANCE_ANNOUNCEMENT_TARGETS)
		.describe('What the button opens: none (no button), the shop, the Plus page or an https link'),
	url: z
		.string()
		.refine(isInstanceAnnouncementUrl, 'Only an https link')
		.nullable()
		.describe('The https link the button opens when target is url'),
	tone: z.enum(INSTANCE_ANNOUNCEMENT_TONES).describe('Bar color; purple is the Micup brand color'),
	starts_at: z.iso.datetime().nullable().describe('Shown from this time; null: right away'),
	ends_at: z.iso.datetime().nullable().describe('Hidden from this time; null: until turned off'),
});

type InstanceAnnouncementFields = z.infer<typeof InstanceAnnouncementFields>;

function checkAnnouncement(value: InstanceAnnouncementFields, ctx: z.RefinementCtx): void {
	if (value.enabled && value.text_tr === null) {
		ctx.addIssue({code: 'custom', path: ['text_tr'], message: 'An enabled announcement needs its Turkish text'});
	}
	if (value.target !== 'none' && value.button_text_tr === null) {
		ctx.addIssue({code: 'custom', path: ['button_text_tr'], message: 'A button needs its Turkish label'});
	}
	if (value.target === 'url' && value.url === null) {
		ctx.addIssue({code: 'custom', path: ['url'], message: 'A link button needs its https link'});
	}
	if (value.starts_at !== null && value.ends_at !== null && Date.parse(value.ends_at) <= Date.parse(value.starts_at)) {
		ctx.addIssue({code: 'custom', path: ['ends_at'], message: 'The end must come after the start'});
	}
}

export const InstanceAnnouncementUpdateRequest = InstanceAnnouncementFields.superRefine(checkAnnouncement);
export type InstanceAnnouncementUpdateRequest = z.infer<typeof InstanceAnnouncementUpdateRequest>;

const AnnouncementId = z.string().max(64).describe('Changes whenever the text, button, link, icon or color change');

export const InstanceAnnouncementConfig = InstanceAnnouncementFields.extend({id: AnnouncementId});
export type InstanceAnnouncementConfig = z.infer<typeof InstanceAnnouncementConfig>;

export const StoredInstanceAnnouncementConfig = InstanceAnnouncementConfig.extend({
	enabled: InstanceAnnouncementFields.shape.enabled.default(false),
	icon: InstanceAnnouncementFields.shape.icon.default(null),
	text_tr: InstanceAnnouncementFields.shape.text_tr.default(null),
	text_en: InstanceAnnouncementFields.shape.text_en.default(null),
	button_text_tr: InstanceAnnouncementFields.shape.button_text_tr.default(null),
	button_text_en: InstanceAnnouncementFields.shape.button_text_en.default(null),
	target: InstanceAnnouncementFields.shape.target.default('none'),
	url: InstanceAnnouncementFields.shape.url.default(null),
	tone: InstanceAnnouncementFields.shape.tone.default('purple'),
	starts_at: InstanceAnnouncementFields.shape.starts_at.default(null),
	ends_at: InstanceAnnouncementFields.shape.ends_at.default(null),
	id: AnnouncementId.default(''),
});

// What the discovery document carries while the announcement is enabled; clients check the time window themselves.
// The icon is any key here: a client built before an icon was added still shows the text and skips the icon.
export const InstanceAnnouncementPublic = InstanceAnnouncementConfig.omit({enabled: true}).extend({
	icon: z.string().max(64).nullable().describe('A channel icon key drawn before the text; an unknown key is not drawn'),
});
export type InstanceAnnouncementPublic = z.infer<typeof InstanceAnnouncementPublic>;
